import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Calendar, Shield, Zap, CheckCircle, AlertCircle } from 'lucide-react';
import apiService from '../services/apiService';
import { useI18n } from '../utils/i18n';

const Home = () => {
  const [setupRequired, setSetupRequired] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setupMessage, setSetupMessage] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useI18n();

  useEffect(() => {
    checkSetupStatus();
    
    // Check for setup messages
    const setupStatus = searchParams.get('setup');
    const errorStatus = searchParams.get('error');
    
    if (setupStatus === 'success') {
      setSetupMessage({ type: 'success', message: t('setup.success', 'Setup erfolgreich abgeschlossen! Sie können sich jetzt anmelden.') });
    } else if (setupStatus === 'error') {
      const errorMessage = searchParams.get('message') || t('setup.errorUnknown', 'Unbekannter Fehler beim Setup-Test');
      setSetupMessage({ type: 'error', message: t('setup.testFailed', `Setup-Test fehlgeschlagen: ${errorMessage}`, { message: errorMessage }) });
    } else if (errorStatus === 'access_denied') {
      const errorMessage = searchParams.get('message') || t('common.accessDenied', 'Zugriff verweigert');
      setSetupMessage({ 
        type: 'error', 
        message: `${errorMessage}`,
        isAccessDenied: true
      });
    }
  }, [searchParams]);

  const checkSetupStatus = async () => {
    try {
      const response = await apiService.getHealth();
      setSetupRequired(!response.configured);
      setIsAuthenticated(!!response.authenticated);
    } catch (error) {
      console.error('Error checking setup status:', error);
      setSetupRequired(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loading && setupRequired === false && isAuthenticated) {
      try { window.dispatchEvent(new CustomEvent('tokenRefreshSuccess')); } catch (_) {}
      try {
        const APPLY_KEY = 'lastVisitedPathShouldApply';
        const PATH_KEY = 'lastVisitedPathAfterAuth';
        const shouldApply = sessionStorage.getItem(APPLY_KEY) === '1';
        const path = sessionStorage.getItem(PATH_KEY);
        if (shouldApply && path && path !== '/' && !path.startsWith('/auth/')) {
          // Client übernimmt Restore, kein Redirect hier
          return;
        }
      } catch (_) {}
      navigate('/dashboard', { replace: true });
      setTimeout(() => { try { window.dispatchEvent(new CustomEvent('tokenRefreshSuccess')); } catch (_) {} }, 0);
      // Zusätzlicher Tick, um Header/Listeners sicher zu informieren
      setTimeout(() => { try { window.dispatchEvent(new CustomEvent('tokenRefreshSuccess')); } catch (_) {} }, 300);
    }
  }, [loading, setupRequired, isAuthenticated, navigate]);

  const handleLogin = () => {
    if (setupRequired) {
      navigate('/setup');
    } else {
      apiService.login();
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <p>{t('welcome.loadingStatus', 'Lade Status...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="welcome-screen">
        <h2>{t('welcome.title', 'Willkommen bei KayCee Ticket')}</h2>
        <p>
          {t('welcome.lead', 'Greifen Sie sicher auf Ihre Microsoft 365 E-Mails und Kalender zu. Dieses Interface bietet Ihnen eine moderne und benutzerfreundliche Oberfläche für Ihre Office 365-Daten.')}
        </p>
        
        {setupMessage && (
          <div className={`setup-message ${setupMessage.type}`} style={{
            background: setupMessage.type === 'success' ? 'var(--color-success-bg)' : 'var(--color-error-bg)',
            border: setupMessage.type === 'success' ? '1px solid var(--color-success-border)' : '1px solid var(--color-error-border)',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            {setupMessage.type === 'success' ? (
              <CheckCircle size={24} style={{ color: 'var(--color-success-text)' }} />
            ) : (
              <AlertCircle size={24} style={{ color: 'var(--color-error-text)' }} />
            )}
            <div style={{ 
              color: setupMessage.type === 'success' ? 'var(--color-success-text)' : 'var(--color-error-text)',
              fontWeight: '500'
            }}>
              {setupMessage.message}
            </div>
          </div>
        )}
        
        <div style={{ display: 'flex', justifyContent: 'center', margin: '30px 0' }}>
          {setupRequired ? (
            <button 
              onClick={handleLogin}
              className="btn btn-primary" 
              style={{ fontSize: '18px', padding: '15px 30px' }}
            >
              {t('welcome.button.setupStart', '⚙️ Setup starten')}
            </button>
          ) : (
            <button 
              onClick={handleLogin}
              className="btn btn-primary" 
              style={{ fontSize: '18px', padding: '15px 30px' }}
            >
              {t('welcome.button.signInM365', 'Mit Microsoft 365 anmelden')}
            </button>
          )}
        </div>
        
        <div className="feature-list">
          <ul>
            <li>
              <Mail className="feature-icon" size={24} />
              <div>
                <strong>{t('welcome.feature.email.title', 'E-Mail-Zugriff')}</strong>
                <div style={{ color: 'var(--color-text-muted)', fontSize: '14px' }}>
                  {t('welcome.feature.email.desc', 'Lesen Sie Ihre E-Mails direkt über die Microsoft Graph API')}
                </div>
              </div>
            </li>
            <li>
              <Calendar className="feature-icon" size={24} />
              <div>
                <strong>{t('welcome.feature.calendar.title', 'Kalender-Integration')}</strong>
                <div style={{ color: 'var(--color-text-muted)', fontSize: '14px' }}>
                  {t('welcome.feature.calendar.desc', 'Überblick über Ihre anstehenden Termine und Ereignisse')}
                </div>
              </div>
            </li>
            <li>
              <Shield className="feature-icon" size={24} />
              <div>
                <strong>{t('welcome.feature.oauth.title', 'Sichere OAuth-Authentifizierung')}</strong>
                <div style={{ color: 'var(--color-text-muted)', fontSize: '14px' }}>
                  {t('welcome.feature.oauth.desc', 'Ihre Daten bleiben sicher durch Microsofts OAuth 2.0')}
                </div>
              </div>
            </li>
            <li>
              <Zap className="feature-icon" size={24} />
              <div>
                <strong>{t('welcome.feature.ui.title', 'Moderne Benutzeroberfläche')}</strong>
                <div style={{ color: 'var(--color-text-muted)', fontSize: '14px' }}>
                  {t('welcome.feature.ui.desc', 'Responsive Design für Desktop und Mobile')}
                </div>
              </div>
            </li>
          </ul>
        </div>

        {setupRequired && (
          <div className="setup-notice" style={{
            background: 'var(--color-warning-bg)',
            border: '1px solid var(--color-warning-border)',
            borderRadius: '8px',
            padding: '20px',
            marginTop: '30px',
            textAlign: 'left'
          }}>
            <h4 style={{ color: 'var(--color-warning-text-dark)', marginBottom: '10px' }}>
              {t('welcome.setupNotice.title', '⚙️ Ersteinrichtung erforderlich')}
            </h4>
            <p style={{ color: 'var(--color-warning-text-dark)', marginBottom: '0' }}>
              {t('welcome.setupNotice.desc', 'Diese Anwendung muss zunächst mit Ihrem Microsoft 365 Tenant konfiguriert werden. Klicken Sie auf "Setup starten" für eine interaktive Anleitung.')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Home; 